/**
 * /**
 * === 3 level style logics as the styleUtils ===
 * @date 2025/12/09
 *
 * Created at 2023/08/04 for v0.3 refactoring
 * from map base
 *
 * Removed majority of feature restyling function to `featureRender`,
 * leave style object functions and other simple function here!
 * @2023/09/12
 */
import { Feature } from 'ol';
import { FeatureLike } from 'ol/Feature';
import { Geometry } from 'ol/geom';
import { Style, Fill, Stroke } from 'ol/style';

import {
  FEATURESTATE as FST,
  SymbolType,
  SYMBOL_REVISION as REV,
  DETECTION_MODE as DTM,
  FIELD_MP,
  isDuctLength,
  isPipeLength,
} from '../types';

import {
  FITTINGTYPE,
  FITTINGVER,
  FITTINGDTL,
  DUCK_VERSION_NUMBER as DUCK,
  DEFAULT_EQUIPMENT_FONT_SIZE as DEF,
  LINETHICKNESSFACTOR as LTF,
} from './config';
import { isPoint, isPolygon } from './geometry';

export const composeFont = (fontSize: number, italic?: boolean) => {
  return `bold ${italic ? 'italic' : ''} ${fontSize}px "Open Sans", "Arial Unicode MS", "sans-serif"`;
};

/**
 * Get variant type: DUCT | PIPE | DUCT_LENGTH | PIPE_LENGTH
 * from feature detail, if version in feature is `4`, got `MANUAL` variant
 *
 * @param feature Feature
 */
export const getVariantFrom = (feature: Feature): string | undefined => {
  // these could be user added line or dot
  const version = feature.get(FITTINGVER);
  // console.log(`>>> Got version: ${version}`);

  if (version === REV.M) return DTM.M; // MANUAL variant!
  // this is AI detected symbol
  const detail = feature.get(FITTINGDTL);
  // FIXME: check detail availability
  // @2024/08/16
  if (!detail) {
    console.warn(`## no detail cached in feature!`);
    return undefined;
  }
  const { category } = detail;
  return category as string;
};

export const strokeWidthByZoom = (zoomLevel: number | undefined) => {
  return Math.max(Math.round((zoomLevel || 0) * LTF), 1);
};

/**
 * More reasonable method borrowd from google map algorithm
 *
 * @date 2024/08/07 firest created
 * @log - make it smaller by multiply 0.6 in the hightlight showing text mode - @2025/08/22
 * @log - make it bigger by * 0.8 - @2025/09/19
 *
 * @param roundZL
 * @param ratio multiply ratio augument zoom level change - 2025/10/08
 * @returns
 */
export const zoomLevelToFontSize = (roundZL: number, ratio: number = 1) => {
  const endFontSize = Math.pow(2, 1 + roundZL) * ratio;
  return Math.max(Math.round(endFontSize), DEF);
};

/**
 * Check if is AI detected DUCT LENGTH, it is being rendered as rectanular feature!
 * @param feature
 * @returns
 */
export const isRectDuctLength = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  const isRectFeature = symbolType === SymbolType.RECT;
  const { category, itemType } = feature.get(FITTINGDTL);
  return isRectFeature && isDuctLength(category, itemType);
};

export const isLinePipeLength = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  if (symbolType !== SymbolType.SEGMENT) return false;
  const detail = feature.get(FITTINGDTL);
  if (!detail) return false;
  const { category, itemType } = detail;
  return isPipeLength(category, itemType);
};

export const isTriangleFeature = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.TRIANGLE;
};

export const isDiamondFeature = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.DIAMOND;
};

/**
 * for single line feature checking, normally used to check if allowed to cut
 * @param feature
 * @returns
 */
export const isSegmentFeature = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.SEGMENT;
};

export const isFlexFeature = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.ZIGZAG;
};

/**
 * Check current feature if active or not
 * @param feature
 * @returns true, false
 */
export const isActive = (feature: Feature | FeatureLike) => {
  return !!feature.get(FST.A);
};
/**
 * invisible, and no interactions
 * @param feature
 * @returns
 */
export const isDisabled = (feature: Feature | FeatureLike) => {
  return !!feature.get(FST.D);
};
/**
 * visible but no interactions
 * @param feature
 * @returns
 */
export const isLocked = (feature: Feature | FeatureLike) => {
  return !!feature.get(FST.K);
};

export const isDuckFeature = (feature: Feature | FeatureLike) => {
  const version = feature.get(FITTINGVER);
  return version === DUCK;
};
/**
 * @deprecated
 * @param feature
 * @returns
 */
export const isPointFeature = (feature: Feature | FeatureLike) => {
  const geometry = feature.getGeometry();
  return isPoint(geometry as Geometry);
};

export const isOffsetFeature = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.DIAMOND;
};

export const isEquipmentFeature = (feature: Feature) => {
  // ! geometry changed from Point → Polygon for hit detection; check symbol
  // type only — geometry type is no longer a reliable identity guard.
  // @2026/05/29
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.EQUIPMENT;
};

export const isNoteFeature = (feature: Feature) => {
  const geometry = feature.getGeometry();
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  const isNote = symbolType === SymbolType.NOTE;
  return isPolygon(geometry) && isNote;
};

/**
 * checking polygon zone area feature
 * @param feature
 * @returns
 */
export const isPolygonFeature = (feature: Feature) => {
  return feature.get(FITTINGTYPE) === SymbolType.POLYGON_AREA;
};

/**
 * TODO: ....
 * Check equipment cached multiplier, any other ways?
 *
 * @param feature
 * @returns
 */
export const hasMultiplierInEqmt = (feature: Feature) => {
  const multiplier = feature.get(FIELD_MP);
  return !!multiplier;
};

/**
 * Set eraser style by stroke width
 *
 * @date 2024/04/03
 * @param width dynamic stroke width decided by zoom level
 * @param strokeColors default feature stroke color
 * @returns
 */
export const eraseStyle = (width: number, strokeColor = '#FFFFFF') => {
  return new Style({
    fill: new Fill({
      color: '#FFFFFF', // white
    }),
    stroke: new Stroke({
      color: strokeColor, // white
      width,
    }),
  });
};

/**
 * Gray blue sketch line style
 *
 * @date 2024/05/14
 * @returns
 */
export const minimalStyle = (): Style => {
  const stroke = new Stroke({ color: '#444C87', width: 1 });
  return new Style({ stroke });
};
